import { API_BASE_URL } from "../environments/environment";
import { getWithAuth, postWithAuth } from "../utils/auth";
import {
    FormEntry,
    FormPayload,
    SavePageAnswersPayload,
} from "../types/formEntryType";
import {
    LocationData,
    SessionStartResult,
    LocationValidationResult,
} from "./locationService";

export const getFormEntries = async (
    skip: number,
    paginationAmount: number,
    searchTerm?: string,
    archived: boolean = false,
    sortBy?: string,
    sortOrder?: string,
    filters?: {
        today?: boolean;
        active?: boolean;
        pending?: boolean;
        thisWeek?: boolean;
    },
    advancedFilters?: {
        // Date range filters
        start_date?: string;
        end_date?: string;

        // Company filters
        companies?: string[];

        // Inspector filters
        inspectors?: string[];

        // Client search
        client_search?: string;

        // Advanced status filters (extends basic status filtering)
        statuses?: string[];
    }
) => {
    const params = new URLSearchParams({
        searchInput: searchTerm || "",
        skip: skip.toString(),
        take: paginationAmount.toString(),
        archived: archived.toString(),
    });

    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);

    // Handle advanced date range filters (takes precedence over quick filters)
    if (advancedFilters?.start_date && advancedFilters?.end_date) {
        params.append('start_date', advancedFilters.start_date);
        params.append('end_date', advancedFilters.end_date);
    } else {
        // Fall back to quick date filters if no advanced date range
        if (filters?.today) {
            params.append('date_range', 'today');
        } else if (filters?.thisWeek) {
            params.append('date_range', 'this_week');
        }
    }

    // Handle advanced status filters (takes precedence over quick filters)
    if (advancedFilters?.statuses && advancedFilters.statuses.length > 0) {
        params.append('status', advancedFilters.statuses.join(','));
    } else {
        // Fall back to quick status filters if no advanced status filters
        const statusFilters: string[] = [];
        if (filters?.active) statusFilters.push('active');
        if (filters?.pending) statusFilters.push('pending');

        if (statusFilters.length > 0) {
            params.append('status', statusFilters.join(','));
        }
    }

    // Company filters
    if (advancedFilters?.companies && advancedFilters.companies.length > 0) {
        params.append('company_ids', advancedFilters.companies.join(','));
    }

    // Inspector filters
    if (advancedFilters?.inspectors && advancedFilters.inspectors.length > 0) {
        params.append('inspector_ids', advancedFilters.inspectors.join(','));
    }

    // Client search filter
    if (advancedFilters?.client_search && advancedFilters.client_search.trim()) {
        params.append('client_search', advancedFilters.client_search.trim());
    }

    return getWithAuth(
        `${API_BASE_URL}/formEntries/get-formEntries?${params.toString()}`
    );
};

export const getFormEntryMetrics = async (
    searchTerm?: string,
    archived: boolean = false,
    sortBy?: string,
    sortOrder?: string,
    filters?: {
        today?: boolean;
        active?: boolean;
        pending?: boolean;
        thisWeek?: boolean;
    },
    advancedFilters?: {
        // Date range filters
        start_date?: string;
        end_date?: string;

        // Company filters
        companies?: string[];

        // Inspector filters
        inspectors?: string[];

        // Client search
        client_search?: string;

        // Advanced status filters (extends basic status filtering)
        statuses?: string[];
    }
) => {
    const params = new URLSearchParams({
        archived: archived.toString(),
    });

    if (searchTerm) {
        params.append("searchInput", searchTerm);
    }

    if (sortBy) {
        params.append("sortBy", sortBy);
    }

    if (sortOrder) {
        params.append("sortOrder", sortOrder);
    }

    // Handle advanced date range filters (takes precedence over quick filters)
    if (advancedFilters?.start_date && advancedFilters?.end_date) {
        params.append("start_date", advancedFilters.start_date);
        params.append("end_date", advancedFilters.end_date);
    } else {
        // Fall back to quick date filters if no advanced date range
        if (filters?.today) {
            params.append("date_range", "today");
        } else if (filters?.thisWeek) {
            params.append("date_range", "this_week");
        }
    }

    // Handle advanced status filters (takes precedence over quick filters)
    if (advancedFilters?.statuses && advancedFilters.statuses.length > 0) {
        params.append("statuses", advancedFilters.statuses.join(","));
    } else {
        // Fall back to quick status filters if no advanced status filters
        const statusFilters: string[] = [];
        if (filters?.active) statusFilters.push("active");
        if (filters?.pending) statusFilters.push("pending");
        if (statusFilters.length > 0) {
            params.append("statuses", statusFilters.join(","));
        }
    }

    // Company filters
    if (advancedFilters?.companies && advancedFilters.companies.length > 0) {
        params.append("company_ids", advancedFilters.companies.join(","));
    }

    // Inspector filters
    if (advancedFilters?.inspectors && advancedFilters.inspectors.length > 0) {
        params.append("inspector_ids", advancedFilters.inspectors.join(","));
    }

    // Client search filter
    if (advancedFilters?.client_search && advancedFilters.client_search.trim()) {
        params.append("client_search", advancedFilters.client_search.trim());
    }

    return getWithAuth(`${API_BASE_URL}/formEntries/metrics?${params.toString()}`);
};

export const getFormEntryById = async (form_entryId: number) => {
    return getWithAuth(
        `${API_BASE_URL}/formEntries/get-form_entry/${form_entryId}`
    );
};

export const createExternalFormEntry = async (
    company: string,
    branchId: string | null,
    flow: number,
    recipientName: string,
    designation: "policyholder" | "external_inspector",
    sendOption: "email" | "sms" | "both" | null,
    expiresIn: string = "30m",
    recipientEmail?: string,
    recipientMobile?: string,
    recipientAddress?: string,
    policyNumber?: string
) => {
    return postWithAuth(`${API_BASE_URL}/external-formEntries`, {
        company,
        branch_id: branchId,
        flow,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        recipient_mobile: recipientMobile,
        recipient_address: recipientAddress,
        designation,
        send_email: sendOption === "email" || sendOption === "both",
        send_sms: sendOption === "sms" || sendOption === "both",
        expires_in: expiresIn,
        policy_number: policyNumber,
    });
};

export const sendExternalFormEntryNotification = async (
    externalLinkId: string,
    sendEmail: boolean = true,
    sendSms: boolean = false
) => {
    return postWithAuth(
        `${API_BASE_URL}/external-formEntries/${externalLinkId}/send-notification`,
        {
            send_email: sendEmail,
            send_sms: sendSms,
        }
    );
};

// External FormEntry Form Data Retrieval with Enhanced Status Management
export const getExternalFormEntry = async (external_id: string) => {
    const response = await fetch(
        `${API_BASE_URL}/external-formEntries/form?id=${external_id}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};
export const createFormEntry = async (
    formVersionId: number,
    flowId: number,
    applicationId?: string,
) => {
    return postWithAuth(`${API_BASE_URL}/form-entries/create`, {
        form_version_id: formVersionId,
        flow_id: flowId,
        application_id: applicationId,
    });
};

export const startFormEntry = async (form_entryId: string) => {
    return getWithAuth(
        `${API_BASE_URL}/formEntries/start/${form_entryId}`
    );
};

export const prefillFormEntry = async (form_entryId: string) => {
    return getWithAuth(
        `${API_BASE_URL}/formEntries/prefill/${form_entryId}`
    );
};

export const saveFormPageAnswers = async (
    payload: SavePageAnswersPayload,
    applicationId?: string,
) => {
    // check current URL
    const params = new URLSearchParams(window.location.search);
    const isPrefilled = params.get("prefilled") === "true";

    // build endpoint with optional query param
    let endpoint = `${API_BASE_URL}/form-entries/save-answers`;
    if (isPrefilled) {
        endpoint += `?prefilled=true`;
    }

    // Augment payload with application_id
    const augmentedPayload = { ...payload, application_id: applicationId };

    return postWithAuth(endpoint, augmentedPayload);
};

export const getFormEntryPdf = async (form_entryId: string | number) => {
    return getWithAuth(
        `${API_BASE_URL}/media/get-form_entry-pdf/${form_entryId}`
    );
};

export const assignInspector = async (
    selectedInspectorId: string,
    selectedFormEntryId: string
) => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/assign-inspector-form_entry/${selectedInspectorId}?form_entryId=${selectedFormEntryId}`,
        { inspectorId: selectedInspectorId }
    );
};

export const assignCompany = async (
    selectedCompanyId: string,
    selectedFormEntry: FormEntry
) => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/assign-company-form_entry/${selectedFormEntry.id}`,
        {
            company: selectedCompanyId,
        }
    );
};

export const changeArchiveStatus = async (
    form_entryId: string,
    archived: boolean
) => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/update-archived-status/${form_entryId}`,
        { archived: !archived }
    );
};

export const submitFormEntryChange = async (
    form_entryId: string,
    currentInspectorId: string,
    newInspectorId: string
) => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/submit-form_entry-change/${form_entryId}`,
        {
            currentInspectorId: currentInspectorId,
            newInspectorId: newInspectorId,
        }
    );
};

export const updateFormEntryChange = async (
    form_entryId: string,
    status: string
) => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/update-form_entry-change/${form_entryId}`,
        { status: status }
    );
};

export const getFormEntryChanges = async () => {
    return getWithAuth(
        `${API_BASE_URL}/formEntries/get-form_entry-changes`
    );
};

// External Location APIs (no auth required)
export const startLocationSession = async (
    form_entryId: string,
    location: LocationData
): Promise<SessionStartResult> => {
    const response = await fetch(
        `${API_BASE_URL}/external/${form_entryId}/location`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy,
            }),
        }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};

export const validateLocation = async (
    form_entryId: string,
    location: LocationData
): Promise<LocationValidationResult> => {
    const response = await fetch(
        `${API_BASE_URL}/external/${form_entryId}/validate-location`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy,
            }),
        }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};

// Page completion metadata recording
export const recordPageCompletion = async (
    form_entryId: string,
    formId: string,
    pageId: string,
    flowId: string,
    location: LocationData,
    eventType:
        | "agreement_confirmed"
        | "page_completed"
        | "form_submitted" = "page_completed",
    userId: string = "external_user"
) => {
    const token = localStorage.getItem("external_form_entry_token");
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(
        `${API_BASE_URL}/external/formEntries/${form_entryId}/page-completion`,
        {
            method: "POST",
            headers,
            body: JSON.stringify({
                form_id: formId,
                page_id: pageId,
                flow_id: flowId,
                location: {
                    latitude: location.latitude,
                    longitude: location.longitude,
                    accuracy: location.accuracy,
                    timestamp: new Date().toISOString(),
                },
                type: "external",
                user_id: userId,
                event_type: eventType,
            }),
        }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};

// Internal form_entry page completion metadata recording (no location required)
export const recordInternalPageCompletion = async (
    form_entryId: string,
    formId: string,
    pageId: string,
    flowId: string,
    location: LocationData | null,
    eventType:
        | "agreement_confirmed"
        | "page_completed"
        | "form_submitted" = "page_completed",
    userId?: string
) => {
    const accessToken = localStorage.getItem("accessToken");
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const body: any = {
        form_id: formId,
        page_id: pageId,
        flow_id: flowId,
        type: "internal",
        user_id: userId || "internal_user",
        event_type: eventType,
    };

    // Only include location if provided (for external formEntries that use this endpoint)
    if (location) {
        body.location = {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy,
            timestamp: new Date().toISOString(),
        };
    }

    const response = await fetch(
        `${API_BASE_URL}/external/formEntries/${form_entryId}/page-completion`,
        {
            method: "POST",
            headers,
            body: JSON.stringify(body),
        }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};

// Complete External FormEntry
export const completeExternalFormEntry = async (externalLinkId: string) => {
    const token = localStorage.getItem("external_form_entry_token");
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(
        `${API_BASE_URL}/external/${externalLinkId}/complete`,
        {
            method: "POST",
            headers,
            body: JSON.stringify({}),
        }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};

export const resendFormEntryOutcome = async (
    form_entryId: string,
    email: string
) => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/${form_entryId}/resend-outcome`,
        { email }
    );
};

export const exportSnapshotToCSV = async (
    searchTerm?: string,
    archived: boolean = false,
    sortBy?: string,
    sortOrder?: string,
    filters?: {
        today?: boolean;
        active?: boolean;
        pending?: boolean;
        thisWeek?: boolean;
    },
    advancedFilters?: {
        // Date range filters
        start_date?: string;
        end_date?: string;

        // Company filters
        companies?: string[];

        // Inspector filters
        inspectors?: string[];

        // Client search
        client_search?: string;

        // Advanced status filters (extends basic status filtering)
        statuses?: string[];
    },
    visibleColumns?: string[]
) => {
    const params = new URLSearchParams({
        searchInput: searchTerm || "",
        archived: archived.toString(),
    });

    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);

    // Handle advanced date range filters (takes precedence over quick filters)
    if (advancedFilters?.start_date && advancedFilters?.end_date) {
        params.append('start_date', advancedFilters.start_date);
        params.append('end_date', advancedFilters.end_date);
    } else {
        // Fall back to quick date filters if no advanced date range
        if (filters?.today) {
            params.append('date_range', 'today');
        } else if (filters?.thisWeek) {
            params.append('date_range', 'this_week');
        }
    }

    // Handle advanced status filters (takes precedence over quick filters)
    if (advancedFilters?.statuses && advancedFilters.statuses.length > 0) {
        params.append('status', advancedFilters.statuses.join(','));
    } else {
        // Fall back to quick status filters if no advanced status filters
        const statusFilters: string[] = [];
        if (filters?.active) statusFilters.push('active');
        if (filters?.pending) statusFilters.push('pending');

        if (statusFilters.length > 0) {
            params.append('status', statusFilters.join(','));
        }
    }

    // Company filters
    if (advancedFilters?.companies && advancedFilters.companies.length > 0) {
        params.append('company_ids', advancedFilters.companies.join(','));
    }

    // Inspector filters
    if (advancedFilters?.inspectors && advancedFilters.inspectors.length > 0) {
        params.append('inspector_ids', advancedFilters.inspectors.join(','));
    }

    // Client search filter
    if (advancedFilters?.client_search && advancedFilters.client_search.trim()) {
        params.append('client_search', advancedFilters.client_search.trim());
    }

    // Add visible columns if provided (exclude "Actions" column)
    if (visibleColumns && visibleColumns.length > 0) {
        const exportableColumns = visibleColumns.filter(col => col !== 'Actions');
        if (exportableColumns.length > 0) {
            params.append('columns', exportableColumns.join(','));
        }
    }

    // Use fetch directly to handle blob response
    const token = typeof window !== 'undefined' ? localStorage.getItem("accessToken") : null;

    if (!token) {
        throw new Error('No authentication token found. Please log in again.');
    }

    const response = await fetch(
        `${API_BASE_URL}/formEntries/snapshot/export?${params.toString()}`,
        {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.statusText}`);
    }

    // Get the blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `snapshot-report-${new Date().toISOString().split('T')[0]}.csv`;

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
        }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'CSV exported successfully' };
};
/**
 * Re-send a timed-out form_entry
 *
 * This operation will:
 * - Archive the original timed-out form_entry
 * - Create a new form_entry with the same details
 * - Migrate only pre-filled answers
 * - Automatically send notification to recipient
 *
 * @param form_entryId - ID of the timed-out form_entry
 * @returns Promise with response containing new form_entry details
 */
export const resendTimedOutFormEntry = async (
    form_entryId: string
): Promise<any> => {
    return postWithAuth(
        `${API_BASE_URL}/formEntries/${form_entryId}/resend-timed-out`,
        {}  // Empty body, endpoint accepts no parameters
    );
};

/**
 * Persist form answers to the form_entries.saved_answers column.
 * Uses session_id to find existing form_entry (must be created when user starts form).
 * Uses the form-builder backend (API_BASE_URL), not the client backend.
 */
export const persistFormAnswers = async (
    payload: {
        session_id: string;  // Source of truth - backend finds form_entry
        application_id: string;
        page_number: number;
        answers: Record<string, any>;
        mark_as_complete?: boolean;
    },
    apiBaseUrl?: string,  // Optional backend URL (defaults to API_BASE_URL from environment)
) => {
    const baseUrl = apiBaseUrl || API_BASE_URL;
    const url = `${baseUrl}/api/v1/form-entries/save-answers`;
    return postWithAuth(url, payload);
};

/**
 * Create a form entry for persist answers functionality.
 * Uses the form-builder backend (API_BASE_URL), not the client backend.
 * Called when persistAnswers is enabled but no form_entry_id exists yet.
 *
 * @param payload - Form entry creation payload
 * @param apiBaseUrl - Optional backend URL (defaults to API_BASE_URL from environment)
 * @returns Promise with the created form entry (includes form_entry_id as data.id)
 */
export const createFormEntryForPersist = async (
    payload: {
        form_version_id: number;
        application_id: string;
        session_id?: string;
    },
    apiBaseUrl?: string,
): Promise<{ code: number; data?: { id: number }; message?: string }> => {
    const baseUrl = apiBaseUrl || API_BASE_URL;
    const url = `${baseUrl}/api/v1/form-entries/create`;
    return postWithAuth(url, payload);
};

/**
 * Fetch saved answers from form_entries table by session_id.
 * Uses the form-builder backend (API_BASE_URL).
 * Returns previously saved answers for the session.
 *
 * @param sessionId - Session ID to fetch answers for
 * @param apiBaseUrl - Optional backend URL (defaults to API_BASE_URL from environment)
 * @returns Promise with saved answers response
 */
export const getSavedAnswersBySession = async (
    sessionId: string,
    apiBaseUrl?: string,
): Promise<{ code: number; data?: { answers: Record<string, any>; session_id: string; form_entry_id: number; form_version_id: number; form_id: number; page_number: number }; message?: string }> => {
    const baseUrl = apiBaseUrl || API_BASE_URL;
    const url = `${baseUrl}/api/v1/form-entries/saved-answers?session_id=${encodeURIComponent(sessionId)}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.status === 404) {
        return {
            code: 404,
            data: {
                answers: {},
                session_id: sessionId,
                form_entry_id: 0,
                form_version_id: 0,
                form_id: 0,
                page_number: 0,
            },
            message: 'No saved answers found',
        };
    }

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};
